import { CAPSTONE, CENTRAL_STATION } from '@rsos/constants/adminContents';
import {
  MANAGE_AGENCY_INFO,
  MANAGE_ALERT_PARTNERS,
  MANAGE_COMMUNICATION,
  MANAGE_DATA_SOURCES,
  MANAGE_INTEGRATIONS,
  MANAGE_KEYWORDS,
  MANAGE_PEER_AGENCIES,
  MANAGE_RESPONDERS,
  MANAGE_SECURITY,
  MANAGE_SOUND,
  MANAGE_SSO,
  MANAGE_USERS,
} from '@rsos/constants/pageAccess';
import type { AccessDescriptor } from '@rsos/hooks/hasAccess';
import {
  AGENCY_SHARE_CHAT,
  AIR_AMBULANCE,
  ALERTS_NOTIFICATION,
  ALERTS_TRAIN_INCIDENT,
  DISCREPANCY_REPORTING,
  GIS_SERVICES,
  UNITE_SUMMARIZATION_AND_KEYWORDS,
} from './capabilities';

/**
 * Access rules for every /admin route, keyed by the full path a user navigates
 * to. For a section whose landing page is an index route, the key is the
 * section path itself (e.g. '/admin/psap'); where a section's children need
 * different permissions, each child is keyed in full (e.g. '/admin/access/*').
 *
 * This is the single source of truth for both route access and sidebar
 * visibility, so the two cannot drift: the sidebar items (rspSideBarItems /
 * cspSideBarItems) look their access up from here by `link`, and the route table
 * (admin's app/Main.tsx) guards each leaf by the same key. See AccessDescriptor
 * (@rsos/hooks/hasAccess) for what each field means and how they combine.
 *
 * `applications` is declared on every route that serves one of RSP/CSP and not
 * the other. Which sidebar an item appears in used to be the only thing
 * expressing that, and a typed URL does not consult the sidebar.
 *
 * The order of the keys is load-bearing: it is the preference order for "where
 * should this user land", used by bare /admin and by the top-nav Admin link
 * (useAdminLandingPath). It mirrors the sidebar order so that landing page is
 * always the topmost sidebar item the user can see. `/admin/notifications` sits
 * next to `/admin/settings`, the RSP route for the same feature, which is what
 * lets one order satisfy both sidebars — a central_station user's reachable
 * subset then reads in cspSideBarItems order.
 *
 * Adding a route to admin's app/Main.tsx without adding it here fails the parity
 * test in the admin app, as does a sidebar item whose `link` has no entry, an
 * entry whose `applications` disagrees with the sidebars the item is listed in,
 * or an order that no longer matches the sidebars.
 */
export const adminRouteAccess: Record<string, AccessDescriptor> = {
  '/admin/psap': { permission: MANAGE_AGENCY_INFO },

  '/admin/access/users': { permission: MANAGE_USERS },
  '/admin/access/roles': {
    permission: MANAGE_USERS,
    applications: [CAPSTONE],
  },

  '/admin/data-source/roles': {
    permission: MANAGE_DATA_SOURCES,
    applications: [CAPSTONE],
  },
  '/admin/data-source/discrepancy-reporting': {
    permission: MANAGE_AGENCY_INFO,
    capabilityAccess: DISCREPANCY_REPORTING,
    applications: [CAPSTONE],
  },
  '/admin/data-source/map-layers': {
    permission: MANAGE_AGENCY_INFO,
    capabilityAccess: GIS_SERVICES,
    applications: [CAPSTONE],
  },

  '/admin/alerts-routing': {
    permission: MANAGE_ALERT_PARTNERS,
    capabilityAccess: ALERTS_NOTIFICATION,
    applications: [CAPSTONE],
  },
  '/admin/share-and-chat': {
    permission: MANAGE_PEER_AGENCIES,
    capabilityAccess: AGENCY_SHARE_CHAT,
    applications: [CAPSTONE],
  },
  '/admin/responders': {
    permission: MANAGE_RESPONDERS,
    capabilityAccess: ALERTS_TRAIN_INCIDENT,
    applications: [CAPSTONE],
  },
  '/admin/messaging': {
    permission: MANAGE_COMMUNICATION,
    applications: [CAPSTONE],
  },

  // Sound/notification settings: one route per application for what is the same
  // feature — RSP's is /admin/settings (the nav labels it "Notifications"), CSP's
  // is /admin/notifications — kept adjacent so one key order can mirror both
  // sidebars.
  //
  // They ask for different permissions only because the two applications
  // register different ones. MANAGE_SOUND is the permission that describes this
  // page — its display_name is literally "Manage Notifications" — and RSP uses
  // it. But MANAGE_SOUND is declared only in capstone_permissions.yaml, so no
  // central_station role can hold it, and CSP keeps the MANAGE_USERS its sidebar
  // item required before these descriptors existed. The write endpoint accepts
  // either: scorpius authorizes org attributes with the any-of
  // `authorize_multiple_permissions`.
  //
  // MANAGE_USERS is a poor fit on its own terms — "can add/remove users to/from
  // their organization" has nothing to do with sound — so the CSP row wants to
  // become MANAGE_SOUND too. Doing that means first declaring MANAGE_SOUND in
  // central_station_permissions.yaml and backfilling it onto the CSP roles that
  // hold MANAGE_USERS today; tightening this line on its own takes the page away
  // from every CSP admin who currently has it.
  '/admin/settings': {
    permission: MANAGE_SOUND,
    applications: [CAPSTONE],
  },
  '/admin/notifications': {
    permission: MANAGE_USERS,
    applications: [CENTRAL_STATION],
  },

  '/admin/keywords': {
    permission: MANAGE_KEYWORDS,
    capabilityAccess: UNITE_SUMMARIZATION_AND_KEYWORDS,
    applications: [CAPSTONE],
  },
  '/admin/access/security': {
    permission: MANAGE_SECURITY,
    applications: [CAPSTONE],
  },
  '/admin/landing-zones': {
    // MANAGE_USERS is what the sidebar item required before these descriptors
    // existed, kept as-is. It is not the permission this page would pick for
    // itself — the page is still a placeholder, so there is no endpoint to derive
    // one from, and the gating that does the work is the AIR_AMBULANCE capability
    // plus the flag, both off by default. Worth settling when the page does
    // something; until then this changes nothing either way.
    permission: MANAGE_USERS,
    capabilityAccess: AIR_AMBULANCE,
    flagAccess: 'manage_landing_zones',
    applications: [CAPSTONE],
  },

  '/admin/integrations/inbound': {
    permission: MANAGE_INTEGRATIONS,
    applications: [CAPSTONE],
  },
  '/admin/integrations/outbound': {
    permission: MANAGE_INTEGRATIONS,
    applications: [CAPSTONE],
  },

  '/admin/access/sso': {
    permission: MANAGE_SSO,
    // Derived from the permission name rather than declared in capabilities.
    capabilityAccess: `${MANAGE_SSO}_0`.toLowerCase(),
  },
};

export default adminRouteAccess;
